import { Injectable } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { environment } from 'src/environments/environment'
import { I18n } from './i18n.service'

/**
 * The copy this app owns inside a device calendar event.
 *
 * A calendar event is two different things glued together: the habit or action's
 * own title and body, which are path-authored, and the lines below, which are
 * ours. Only ours belong in the messages catalog — path-authored content is
 * unbounded and passes through untouched.
 */
@Injectable({ providedIn: 'root' })
export class CalendarCopyService {
  constructor(private i18n: I18n, private title: Title) {
  }

  /**
   * The build is white-labelled, so the brand has to be a value rather than part
   * of the catalog key — otherwise every brand needs its own near-duplicate
   * translation. This is the same source `setUserAppName()` reads to tell the
   * backend which app the user is on.
   */
  get appName(): string {
    return this.title.getTitle()
  }

  habitLabel(title: any): string {
    return `${this.i18n.t('Habit:')} ${title}`
  }

  actionLabel(title: any): string {
    return `${this.i18n.t('Action:')} ${title}`
  }

  /**
   * Notes for an action event.
   *
   * `body` is the habit's own text, already stripped of HTML by the caller.
   * `link` is the tool attached to it, when there is one. `expiresOn` is set
   * only for presigned URLs, which stop working after a week — everything else
   * points at the Library instead.
   */
  actionNotes(body: any, link?: any, expiresOn?: string): string {
    const lines = [body]

    if (link) {
      lines.push(this.toolLine(link))
      lines.push(expiresOn ? this.expiryLine(expiresOn) : this.libraryLine())
    }

    lines.push(this.completionLine())

    return lines.join('\n\n')
  }

  /**
   * Variant used by the habit list, where the action cannot be completed from a
   * deep link and the user is pointed at the in-app bell instead.
   */
  bellReminderNotes(body: any, link: any, deepLink: string): string {
    return [
      body,
      this.toolLine(link),
      this.i18n.t(
        'To mark this action complete in the %{appName} app, tap the bell icon at the top of your screen and select this action from the list. Tap here on mobile to open the %{appName} app: %{link}',
        { appName: this.appName, link: deepLink }
      )
    ].join('\n\n')
  }

  /** Notes for the daily/weekly learning reminder created during onboarding. */
  nextStepNotes(): string {
    return this.i18n.t(
      'Click here or open the %{appName} app to take your next step: %{link}',
      { appName: this.appName, link: environment.actionDeepLinkURL }
    )
  }

  private toolLine(link: any): string {
    return this.i18n.t(
      'Here is the tool you saw in the %{appName} app to help you complete this action: %{link}',
      { appName: this.appName, link }
    )
  }

  private expiryLine(expiresOn: string): string {
    return this.i18n.t(
      'This link will expire on %{date}. To see and edit this tool any time, open your Library in the app',
      { date: expiresOn }
    )
  }

  private libraryLine(): string {
    return this.i18n.t('To see and edit this tool any time, open your Library in the app')
  }

  private completionLine(): string {
    return this.i18n.t(
      'After you have completed this action, mark it complete in the %{appName} app by clicking here: %{link}',
      { appName: this.appName, link: environment.actionDeepLinkURL }
    )
  }
}
